import { PDFDocument, StandardFonts, rgb, type PDFPage } from "pdf-lib";
import type {
  MetaAdsReportData,
  AgencyBrandConfig,
  MetaAdsDailyRow,
  MetaAdsCampaignRow,
} from "@agencylens/shared";
import {
  MARGIN_X,
  PAGE_H,
  PAGE_W,
  BODY,
  drawHeader,
  drawFooter,
  drawKpiGrid,
  drawTable,
  drawFallbackLineChart,
  fmtDateRange,
  fmtNumber,
  fmtPercent,
  type PDFFonts,
  type TableColumn,
} from "./render-utils.js";

export interface RenderMetaAdsOptions {
  data: MetaAdsReportData;
  agency: AgencyBrandConfig;
  chartPng?: Uint8Array;
}

function currencySymbol(code: string): string {
  switch (code) {
    case "USD":
    case "CAD":
    case "AUD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    case "JPY":
      return "¥";
    default:
      return `${code} `;
  }
}

function fmtMoney(n: number, currency: string): string {
  return `${currencySymbol(currency)}${fmtNumber(n)}`;
}

function truncate(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1) + "…";
}

/* --------------------------------------------------- summary page */

function drawSummaryPage(
  page: PDFPage,
  fonts: PDFFonts,
  data: MetaAdsReportData,
  brand: AgencyBrandConfig,
): void {
  drawHeader(page, fonts, brand, "Meta Ads Report");

  let y = PAGE_H - 120;
  page.drawText(data.account.name, {
    x: MARGIN_X,
    y,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  y -= 18;
  page.drawText(
    `${data.account.adAccountId}${data.account.businessName ? " · " + data.account.businessName : ""} · ${fmtDateRange(data.startDate, data.endDate)}`,
    {
      x: MARGIN_X,
      y,
      size: 10,
      font: fonts.regular,
      color: rgb(0.45, 0.45, 0.45),
    },
  );
  y -= 32;

  const ccy = data.account.currency || "USD";
  drawKpiGrid(
    page,
    fonts,
    [
      { label: "Spend", value: fmtMoney(data.totals.spend, ccy) },
      { label: "Impressions", value: fmtNumber(data.totals.impressions) },
      { label: "Clicks", value: fmtNumber(data.totals.clicks) },
      { label: "Reach", value: fmtNumber(data.totals.reach) },
      { label: "CPM", value: fmtMoney(data.totals.cpm, ccy) },
      { label: "CPC", value: fmtMoney(data.totals.cpc, ccy) },
    ],
    y,
  );
}

/* --------------------------------------------------- chart page */

async function drawChartPage(
  doc: PDFDocument,
  page: PDFPage,
  fonts: PDFFonts,
  data: MetaAdsReportData,
  brand: AgencyBrandConfig,
  chartPng?: Uint8Array,
): Promise<void> {
  drawHeader(page, fonts, brand, "Meta Ads Report");
  page.drawText("Spend over time", {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });

  if (chartPng) {
    const png = await doc.embedPng(chartPng);
    const maxW = PAGE_W - 2 * MARGIN_X;
    const maxH = BODY - 160;
    const aspect = png.width / png.height;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    page.drawImage(png, {
      x: MARGIN_X + (maxW - w) / 2,
      y: PAGE_H - 140 - h,
      width: w,
      height: h,
    });
    return;
  }
  drawFallbackLineChart(
    page,
    fonts,
    brand,
    data.daily.map((d) => ({ date: d.date, value: d.spend })),
    { topY: PAGE_H - 150 },
  );
}

/* --------------------------------------------------- campaigns page */

function makeCampaignColumns(
  currency: string,
): TableColumn<MetaAdsCampaignRow>[] {
  return [
    { label: "Campaign", width: 200, format: (r) => truncate(r.campaignName, 35) },
    { label: "Objective", width: 100, format: (r) => r.objective },
    {
      label: "Spend",
      width: 80,
      align: "right",
      format: (r) => fmtMoney(r.spend, currency),
    },
    { label: "Clicks", width: 60, align: "right", format: (r) => fmtNumber(r.clicks) },
    { label: "Results", width: 60, align: "right", format: (r) => fmtNumber(r.results) },
  ];
}

function drawCampaignsPage(
  page: PDFPage,
  fonts: PDFFonts,
  rows: MetaAdsCampaignRow[],
  brand: AgencyBrandConfig,
  currency: string,
): void {
  drawHeader(page, fonts, brand, "Meta Ads Report");
  page.drawText("Top campaigns", {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  drawTable(page, fonts, brand, makeCampaignColumns(currency), rows, PAGE_H - 150);
}

/* --------------------------------------------------- daily table */

function makeDailyColumns(currency: string): TableColumn<MetaAdsDailyRow>[] {
  return [
    { label: "Date", width: 80, format: (r) => r.date },
    {
      label: "Impressions",
      width: 100,
      align: "right",
      format: (r) => fmtNumber(r.impressions),
    },
    { label: "Clicks", width: 70, align: "right", format: (r) => fmtNumber(r.clicks) },
    {
      label: "Spend",
      width: 90,
      align: "right",
      format: (r) => fmtMoney(r.spend, currency),
    },
    { label: "Results", width: 70, align: "right", format: (r) => fmtNumber(r.results) },
  ];
}

function drawDailyTablePage(
  page: PDFPage,
  fonts: PDFFonts,
  rows: MetaAdsDailyRow[],
  brand: AgencyBrandConfig,
  currency: string,
  pageLabel: string,
): void {
  drawHeader(page, fonts, brand, "Meta Ads Report");
  page.drawText(pageLabel, {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  drawTable(page, fonts, brand, makeDailyColumns(currency), rows, PAGE_H - 150);
}

/* --------------------------------------------------- main render */

const ROWS_PER_TABLE_PAGE = 31;

export async function renderMetaAdsReport(
  opts: RenderMetaAdsOptions,
): Promise<Uint8Array> {
  const { data, agency, chartPng } = opts;
  const doc = await PDFDocument.create();
  doc.setTitle(`${agency.agencyName} — Meta Ads ${data.account.name}`);
  doc.setAuthor(agency.agencyName);
  doc.setProducer("AgencyLens");
  doc.setCreator("AgencyLens");
  doc.setCreationDate(new Date());

  const regular = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const fonts: PDFFonts = { regular, bold };
  const currency = data.account.currency || "USD";

  const summary = doc.addPage([PAGE_W, PAGE_H]);
  drawSummaryPage(summary, fonts, data, agency);

  const chartPage = doc.addPage([PAGE_W, PAGE_H]);
  await drawChartPage(doc, chartPage, fonts, data, agency, chartPng);

  const campaignsPage = doc.addPage([PAGE_W, PAGE_H]);
  drawCampaignsPage(campaignsPage, fonts, data.campaigns.slice(0, 25), agency, currency);

  const chunks: MetaAdsDailyRow[][] = [];
  for (let i = 0; i < data.daily.length; i += ROWS_PER_TABLE_PAGE) {
    chunks.push(data.daily.slice(i, i + ROWS_PER_TABLE_PAGE));
  }
  if (chunks.length === 0) chunks.push([]);

  chunks.forEach((chunk, idx) => {
    const page = doc.addPage([PAGE_W, PAGE_H]);
    const label =
      chunks.length === 1
        ? "Daily breakdown"
        : `Daily breakdown (${idx + 1} of ${chunks.length})`;
    drawDailyTablePage(page, fonts, chunk, agency, currency, label);
  });

  const pages = doc.getPages();
  const total = pages.length;
  pages.forEach((p, i) => drawFooter(p, fonts, agency, i + 1, total));

  return doc.save();
}

// Re-export to satisfy ad-hoc imports if needed.
export { fmtPercent };
